// SPDX-License-Identifier: MIT
// « Où est le dossier » — badge d'état de continuité multi-postes (per-projet).
// Sœur visuelle de cloud-save-indicator + ink-gauge : même langage carnet/encre.
// Un projet = un dossier d'atelier ; le badge dit où il est par rapport à CE poste.
//   here       → ruban sage « Sur ce poste » (le dossier est ouvert sur le bureau)
//   cloud      → ruban neutre + bouton « Reprendre sur ce poste » (il est au coffre)
//   local-only → ruban neutre + bouton « Sauvegarder dans le cloud »
//   diverged   → CARTE orange d'AVERTISSEMENT (rien n'est écrasé)
// Pendant la reprise, renderResumeProgress rend un ruban d'avancement HONNÊTE
// (clonage→lecture→restauration→✓) ou une carte orange en cas d'échec, avec le
// VRAI message — jamais un échec muet (la cause des 3 sessions de churn).
import { t, setBundleLocale } from '../i18n/t';
import type { Locale } from '../i18n/detect';
import type { ProjectMachineState } from '../platform/project-machine-state';
import type { ResumeResult } from '../platform/resume-on-this-machine';

// ── glyphes feuillet (line-art, currentColor) — parenté avec « le double » ──
// dossier posé sur le plan de travail (sur ce poste)
const G_HERE =
  '<svg class="continuity__glyph" viewBox="0 0 18 18" fill="none" aria-hidden="true">' +
  '<rect x="3.5" y="2.4" width="10" height="11" rx="1.5" stroke="currentColor" stroke-width="1.3" fill="var(--cream)"/>' +
  '<path d="M6 6h5M6 8.4h5M6 10.8h3" stroke="currentColor" stroke-width="1.1" stroke-linecap="round"/>' +
  '<path d="M2 15.4h14" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>';
// dossier au coffre, à rapatrier (chevron vers le bas = le ramener ici)
const G_CLOUD =
  '<svg class="continuity__glyph" viewBox="0 0 18 18" fill="none" aria-hidden="true">' +
  '<rect x="2.4" y="2.6" width="13.2" height="8.4" rx="1.6" stroke="currentColor" stroke-width="1.3" fill="color-mix(in srgb,currentColor 8%,transparent)"/>' +
  '<path d="M5.4 5.6h7.2M5.4 8h4.4" stroke="currentColor" stroke-width="1.1" stroke-linecap="round"/>' +
  '<path d="M9 12.4v3.2M6.7 13.6 9 15.8l2.3-2.2" stroke="currentColor" stroke-width="1.35" stroke-linecap="round" stroke-linejoin="round"/></svg>';
// feuillet seul, pas encore au coffre (chevron vers le haut)
const G_LOCAL =
  '<svg class="continuity__glyph" viewBox="0 0 18 18" fill="none" aria-hidden="true">' +
  '<rect x="4" y="5" width="10" height="11" rx="1.5" stroke="currentColor" stroke-width="1.3" fill="var(--cream)"/>' +
  '<path d="M6.3 8.4h5.4M6.3 10.8h3.4" stroke="currentColor" stroke-width="1.1" stroke-linecap="round"/>' +
  '<path d="M9 4.2V1M7 3l2-2 2 2" stroke="currentColor" stroke-width="1.35" stroke-linecap="round" stroke-linejoin="round"/></svg>';
// transit (clonage/lecture/restauration) — réutilise le coffre, « respire » en CSS
const G_PROGRESS = G_CLOUD;
// classé sur ce poste (coche d'encre)
const G_DONE =
  '<svg class="continuity__glyph" viewBox="0 0 18 18" fill="none" aria-hidden="true">' +
  '<rect x="3.5" y="2.4" width="10" height="11" rx="1.5" stroke="currentColor" stroke-width="1.3" fill="var(--cream)"/>' +
  '<path d="M6 9.4 8 11.4l3.4-3.8" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/>' +
  '<path d="M2 15.4h14" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>';
// divergence : deux feuillets qui s'écartent (les deux postes ont divergé)
const G_SPLIT =
  '<svg class="continuity-card__glyph" viewBox="0 0 30 30" fill="none" aria-hidden="true">' +
  '<rect x="2.5" y="6" width="13" height="17" rx="2" transform="rotate(-7 9 14.5)" stroke="currentColor" stroke-width="1.6" fill="color-mix(in srgb,currentColor 9%,transparent)"/>' +
  '<rect x="14.5" y="6" width="13" height="17" rx="2" transform="rotate(7 21 14.5)" stroke="currentColor" stroke-width="1.6" fill="var(--paper-warm)"/></svg>';
// échec : feuillet barré (avec le vrai message à côté)
const G_FAIL =
  '<svg class="continuity-card__glyph" viewBox="0 0 30 30" fill="none" aria-hidden="true">' +
  '<rect x="6.5" y="4" width="17" height="22" rx="2.2" stroke="currentColor" stroke-width="1.6" fill="color-mix(in srgb,currentColor 8%,transparent)"/>' +
  '<path d="M11 21.5 19 9.5" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/></svg>';
// case cochée — note « la construction revient après reprise »
const G_CHECKBOX =
  '<svg viewBox="0 0 18 18" fill="none" aria-hidden="true">' +
  '<rect x="3" y="3" width="12" height="12" rx="2" stroke="currentColor" stroke-width="1.3"/>' +
  '<path d="M6.5 9.2 8.3 11l3.2-3.6" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/></svg>';

// Split a string on **bold** markers → text + <strong> nodes (cheap, no nesting).
function appendRich(parent: HTMLElement, text: string): void {
  text.split('**').forEach((p, i) => {
    if (p === '') return;
    if (i % 2 === 1) {
      const s = document.createElement('strong');
      s.textContent = p;
      parent.appendChild(s);
    } else {
      parent.appendChild(document.createTextNode(p));
    }
  });
}

export interface ContinuityBadgeOptions {
  locale?: Locale;
  onResume?: () => void;     // cloud → « Reprendre sur ce poste »
  onSave?: () => void;       // local-only → « Sauvegarder dans le cloud »
  onRecover?: () => void;    // diverged → « Récupérer la version cloud »
  onKeepLocal?: () => void;  // diverged → « Garder la version locale »
}

function actionButton(label: string, cls: string, onClick?: () => void): HTMLButtonElement {
  const b = document.createElement('button');
  b.type = 'button';
  b.className = cls;
  b.textContent = label;
  if (onClick) b.addEventListener('click', onClick);
  return b;
}

function ribbon(stateClass: string, hue: string, glyph: string): HTMLElement {
  const el = document.createElement('span');
  el.className = `continuity continuity--${stateClass}`;
  el.dataset.component = 'continuity-badge';
  el.dataset.state = stateClass;
  el.setAttribute('role', 'status');
  el.style.setProperty('--hue', hue);
  const g = document.createElement('span');
  g.innerHTML = glyph;
  el.appendChild(g.firstElementChild as SVGElement);
  return el;
}

function labelSpan(text: string): HTMLElement {
  const s = document.createElement('span');
  s.className = 'continuity__label';
  s.textContent = text;
  return s;
}

// Per-project state badge. `here|cloud|local-only` → ribbon ; `diverged` → card.
export function renderContinuityBadge(state: ProjectMachineState, opts: ContinuityBadgeOptions = {}): HTMLElement {
  if (opts.locale) setBundleLocale(opts.locale);

  if (state === 'diverged') {
    const card = document.createElement('div');
    card.className = 'continuity-card';
    card.dataset.component = 'continuity-badge';
    card.dataset.state = 'diverged';
    const g = document.createElement('span'); g.innerHTML = G_SPLIT;
    card.appendChild(g.firstElementChild as SVGElement);
    const body = document.createElement('div'); body.className = 'continuity-card__body';
    const title = document.createElement('p'); title.className = 'continuity-card__title'; title.textContent = t('continuity.diverged.title');
    const sub = document.createElement('p'); sub.className = 'continuity-card__sub'; sub.textContent = t('continuity.diverged.sub');
    const actions = document.createElement('div'); actions.className = 'continuity-card__actions';
    actions.append(
      actionButton(t('continuity.diverged.recover'), 'continuity-card__btn continuity-card__btn--primary', opts.onRecover),
      actionButton(t('continuity.diverged.keepLocal'), 'continuity-card__btn continuity-card__btn--ghost', opts.onKeepLocal),
    );
    const note = document.createElement('p'); note.className = 'continuity-card__note'; note.textContent = t('continuity.diverged.note');
    body.append(title, sub, actions, note);
    card.appendChild(body);
    return card;
  }

  if (state === 'here') {
    const el = ribbon('here', 'var(--sage-deep)', G_HERE);
    el.appendChild(labelSpan(t('continuity.here')));
    const hand = document.createElement('span'); hand.className = 'continuity__hand'; hand.textContent = t('continuity.hereHand');
    el.appendChild(hand);
    return el;
  }

  if (state === 'local-only') {
    const el = ribbon('local', 'var(--muted)', G_LOCAL);
    el.appendChild(labelSpan(t('continuity.localOnly')));
    el.appendChild(actionButton(t('continuity.saveCloud'), 'continuity__action', opts.onSave));
    return el;
  }

  // 'cloud' — in the cloud, not on this machine yet → offer Resume
  const el = ribbon('cloud', 'var(--muted)', G_CLOUD);
  el.appendChild(labelSpan(t('continuity.cloud')));
  el.appendChild(actionButton(t('continuity.resume'), 'continuity__action', opts.onResume));
  return el;
}

export interface ResumePanelOptions {
  locale?: Locale;
  onResume?: () => void;   // « ⬇ Reprendre sur ce poste » — clone + hydrate THIS project here
  onAdopt?: () => void;    // « J'ai déjà ce projet ici » — adopt an existing local clone
}

// One legend row (« qui fait quoi ») — a mono key chip + a rich value.
function legendRow(here: boolean, key: string, value: string): HTMLElement {
  const r = document.createElement('div');
  r.className = `resume-legend__row${here ? ' resume-legend__row--here' : ''}`;
  const k = document.createElement('span'); k.className = 'resume-legend__k'; k.textContent = key;
  const v = document.createElement('span'); v.className = 'resume-legend__v'; appendRich(v, value);
  r.append(k, v);
  return r;
}

// Prominent cloud-state panel — LEADS the project card body when a project lives
// in the cloud but isn't on this machine yet. « ⬇ Reprendre sur ce poste » is the
// ONE clear action (clone + hydrate this project's code+history here). The build
// steps stay hidden until the project is back (no recreation), and a legend kills
// the « Récupérer mes projets » (account list) vs « Reprendre » (this project's
// code) confusion. The progress host (.resume-panel__progress) receives
// renderResumeProgress so the reprise stays observable. Replaces the small
// inline cloud ribbon at the desktop delivery call site (cf the buried-in-rebuild
// feedback : the resume action belongs at the TOP, not under « construire en local »).
export function renderResumePanel(opts: ResumePanelOptions = {}): HTMLElement {
  if (opts.locale) setBundleLocale(opts.locale);

  const block = document.createElement('div');
  block.dataset.component = 'resume-panel';

  const panel = document.createElement('div');
  panel.className = 'resume-panel';

  const glyph = document.createElement('span');
  glyph.className = 'resume-panel__glyph';
  glyph.innerHTML = G_CLOUD;
  panel.appendChild(glyph);

  const body = document.createElement('div');
  body.className = 'resume-panel__body';

  const title = document.createElement('p');
  title.className = 'resume-panel__title';
  title.textContent = t('continuity.resumePanel.title');

  const sub = document.createElement('p');
  sub.className = 'resume-panel__sub';
  appendRich(sub, t('continuity.resumePanel.sub'));

  const actions = document.createElement('div');
  actions.className = 'resume-panel__actions';
  const cta = document.createElement('button');
  cta.type = 'button';
  cta.className = 'resume-panel__cta';
  cta.textContent = `⬇ ${t('continuity.resumePanel.cta')}`;
  if (opts.onResume) cta.addEventListener('click', opts.onResume);
  const hand = document.createElement('span');
  hand.className = 'resume-panel__hand';
  hand.textContent = t('continuity.resumePanel.hand');
  actions.append(cta, hand);

  // Secondary escape hatch — adopt an existing clone instead of cloning again.
  const ghost = document.createElement('button');
  ghost.type = 'button';
  ghost.className = 'resume-panel__ghost';
  ghost.setAttribute('data-component', 'adopt-folder');
  ghost.textContent = t('delivery.adoptCta');
  ghost.title = t('delivery.adoptHint');
  if (opts.onAdopt) ghost.addEventListener('click', opts.onAdopt);
  const adoptMsg = document.createElement('p');
  adoptMsg.className = 'resume-panel__adopt-msg';
  adoptMsg.hidden = true;

  // Observable reprise lands here (renderResumeProgress).
  const progress = document.createElement('div');
  progress.className = 'resume-panel__progress';

  body.append(title, sub, actions, ghost, adoptMsg, progress);
  panel.appendChild(body);

  const legend = document.createElement('div');
  legend.className = 'resume-legend';
  legend.append(
    legendRow(false, t('continuity.resumePanel.legendRecoverK'), t('continuity.resumePanel.legendRecoverV')),
    legendRow(true, t('continuity.resumePanel.legendResumeK'), t('continuity.resumePanel.legendResumeV')),
  );
  panel.appendChild(legend);
  block.appendChild(panel);

  // « la construction revient après reprise » — explains why no build buttons show.
  const hidden = document.createElement('div');
  hidden.className = 'resume-build-hidden';
  const hg = document.createElement('span'); hg.innerHTML = G_CHECKBOX;
  const hp = document.createElement('p');
  appendRich(hp, t('continuity.resumePanel.buildHidden'));
  hidden.append(hg.firstElementChild as SVGElement, hp);
  block.appendChild(hidden);

  return block;
}

// Observable resume progress for a ResumeResult. In-progress steps (cloning/
// reading/hydrating) + done + cancelled render a ribbon ; `failed` renders an
// orange card carrying the REAL message + detail (never a silent failure).
export function renderResumeProgress(result: ResumeResult, opts: { locale?: Locale } = {}): HTMLElement {
  if (opts.locale) setBundleLocale(opts.locale);
  const { step, message } = result;

  if (step === 'failed') {
    const card = document.createElement('div');
    card.className = 'continuity-card continuity-card--failed';
    card.dataset.component = 'continuity-progress';
    card.dataset.step = 'failed';
    const g = document.createElement('span'); g.innerHTML = G_FAIL;
    card.appendChild(g.firstElementChild as SVGElement);
    const body = document.createElement('div'); body.className = 'continuity-card__body';
    const title = document.createElement('p'); title.className = 'continuity-card__title'; title.textContent = t('continuity.progress.failed');
    // Newbie-first: a plain, reassuring sentence (what to do — nothing is lost).
    const hint = document.createElement('p'); hint.className = 'continuity-card__sub'; hint.textContent = t('continuity.progress.failedHint');
    body.append(title, hint);
    // The raw technical cause stays available but TUCKED behind a disclosure, so
    // the newbie isn't assaulted by git stderr while the detail is still there
    // for whoever needs it (the observability the redesign was built for).
    if (message) {
      const det = document.createElement('details'); det.className = 'continuity-card__details';
      const sum = document.createElement('summary'); sum.className = 'continuity-card__summary'; sum.textContent = t('continuity.progress.showDetail');
      const raw = document.createElement('div'); raw.className = 'continuity-card__detail'; raw.textContent = message;
      det.append(sum, raw); body.append(det);
    }
    card.appendChild(body);
    return card;
  }

  const isDone = step === 'done';
  const isCancelled = step === 'cancelled';
  const cls = isDone ? 'done' : isCancelled ? 'cancelled' : 'progress';
  const hue = isDone ? 'var(--sage-deep)' : 'var(--muted)';
  const glyph = isDone ? G_DONE : isCancelled ? G_LOCAL : G_PROGRESS;
  const el = ribbon(cls, hue, glyph);
  el.dataset.component = 'continuity-progress';
  el.dataset.step = step;
  const labelKey = isDone ? 'continuity.progress.done'
    : isCancelled ? 'continuity.progress.cancelled'
    : `continuity.progress.${step}`;
  el.appendChild(labelSpan(t(labelKey)));
  if (isDone) {
    const hand = document.createElement('span'); hand.className = 'continuity__hand'; hand.textContent = t('continuity.progress.doneHand');
    el.appendChild(hand);
  }
  return el;
}
